import { computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SessionService, AccessControlService } from '@bizzdesign/auth';
import { type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  type CommentAnchor,
  type CommentDraft,
  type CommentMarkerViewModel,
  type CommentModeLocation,
  type CommentsLoadState,
  type GraphicBoundsResolver,
} from '@bizzdesign/modeler/models';
import {
  type Comment,
  type CommentEvent,
  type CommentParent,
  type CommentThread,
} from '@bizzdesign/sdk-chat';
import { FeatureFlagsStore } from '@bizzdesign/shared/feature-flags';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { Dispatcher } from '@ngrx/signals/events';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  defer,
  distinctUntilChanged,
  EMPTY,
  from,
  map,
  merge,
  type Observable,
  pipe,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import {
  type CommentOperation,
  commentApiEvents,
  commentUiEvents,
} from '../../events/comments/comment.events';
import { CommentsService } from '../../services/comments.service';
import {
  applyCommentEvent,
  type CommentThreadMap,
  mergeThread,
  rootCommentOf,
  sortByCreation,
  toMarkerViewModels,
  toStoredAnchor,
  toThreadMap,
  upsertThread,
} from './comment.store.utils';

/**
 * State of the comments on the current frame or view.
 *
 * @property parent - Frame or view the store is connected to; `null` when disconnected.
 * @property threads - Loaded threads keyed by id.
 * @property hasMoreThreads - `true` when the page cap was hit and older threads were not loaded.
 * @property loadState - Progress of the initial load.
 * @property loadError - Message of the last failed load, if any.
 * @property draft - Marker placed for a thread that has no comment yet.
 * @property selectedThreadId - Thread whose panel is open.
 * @property isCommentModeOn - Whether clicks on the canvas place comment markers. Not persisted.
 * @property areCommentsVisible - Whether markers are rendered at all.
 */
export type CommentLocalState = {
  parent: CommentParent | null;
  threads: CommentThreadMap;
  hasMoreThreads: boolean;
  loadState: CommentsLoadState;
  loadError: string | null;
  draft: CommentDraft | null;
  selectedThreadId: UUID | null;
  isCommentModeOn: boolean;
  areCommentsVisible: boolean;
};

const initialState: CommentLocalState = {
  parent: null,
  threads: {},
  hasMoreThreads: false,
  loadState: 'idle',
  loadError: null,
  draft: null,
  selectedThreadId: null,
  isCommentModeOn: false,
  areCommentsVisible: true,
};

/**
 * Owns comment threads, markers and comment mode for one frame or view.
 *
 * Connect it with {@link CommentLocalStore.connect} once the parent id is
 * known; it subscribes to realtime events first, loads the threads page by
 * page, replays the events buffered meanwhile, and repeats the whole sequence
 * whenever the realtime connection is restored. Nothing is loaded while the
 * feature flag is off or the user has no comment role.
 *
 * @remarks
 * Provide it per modeler instance (component providers), not in root: its
 * state belongs to one frame or view.
 */
export const CommentLocalStore = signalStore(
  { protectedState: false },
  withState(initialState),
  withProps(() => {
    const accessControlService = inject(AccessControlService);
    const sessionService = inject(SessionService);
    return {
      featureFlags: inject(FeatureFlagsStore),
      commentsService: inject(CommentsService),
      dispatcher: inject(Dispatcher),
      commentThreadsAccess: toSignal(
        accessControlService.getActionState$('commentThreads', 'view'),
        { initialValue: 'hidden' as const },
      ),
      currentUserId: toSignal(
        sessionService.authenticatedUser$.pipe(
          map((user) => user?.userId ?? null),
        ),
        { initialValue: null },
      ),
      _graphicBoundsResolver: signal<GraphicBoundsResolver | null>(null),
      // Bumped when graphics move; the resolver keeps its identity, so this is
      // what makes the marker positions recompute.
      _graphicBoundsVersion: signal(0),
    };
  }),
  withComputed((store) => {
    const isCommentingAllowed = computed(
      () =>
        store.featureFlags.frameCommentsEnabled() &&
        store.commentThreadsAccess() === 'accessible',
    );
    return {
      isCommentingAllowed,
      threadList: computed(() =>
        sortByCreation(Object.values(store.threads())),
      ),
      markers: computed<CommentMarkerViewModel[]>(() => {
        store._graphicBoundsVersion();
        return store.areCommentsVisible()
          ? toMarkerViewModels(
              store.threads(),
              store._graphicBoundsResolver(),
              store.currentUserId(),
              store.selectedThreadId(),
            )
          : [];
      }),
      unreadThreadIds: computed(() =>
        toMarkerViewModels(store.threads(), null, store.currentUserId(), null)
          .filter((marker) => marker.hasUnread)
          .map((marker) => marker.threadId),
      ),
      selectedThread: computed<CommentThread | undefined>(() => {
        const id = store.selectedThreadId();
        return id === null ? undefined : store.threads()[id];
      }),
    };
  }),
  withMethods((store) => {
    const isCommentingAllowed$ = toObservable(store.isCommentingAllowed);

    const reportFailure = (operation: CommentOperation, error: unknown) => {
      store.dispatcher.dispatch(
        commentApiEvents.commentOperationFailed({
          operation,
          message: describeError(error),
        }),
      );
    };

    const applyEvent = (event: CommentEvent) => {
      const parent = store.parent();
      if (parent === null) {
        return;
      }
      const threads = applyCommentEvent(store.threads(), event, parent);
      if (threads !== store.threads()) {
        patchState(store, { threads });
      }
    };

    /** Merges a mutation response into the store without dropping newer realtime state. */
    const applyThread = (thread: CommentThread) => {
      const threads = store.threads();
      patchState(store, {
        threads: upsertThread(threads, mergeThread(threads[thread.id], thread)),
      });
    };

    const resetForParent = (parent: CommentParent | null) => {
      patchState(store, {
        ...initialState,
        parent,
        isCommentModeOn: false,
        areCommentsVisible: store.areCommentsVisible(),
      });
    };

    /**
     * One realtime session: subscribe, load, replay, then stay live. Events
     * arriving before the snapshot has been applied are buffered so nothing
     * created in between is lost.
     */
    const session$ = (parent: CommentParent): Observable<unknown> =>
      defer(() => {
        const buffered: CommentEvent[] = [];
        let snapshotApplied = false;
        patchState(store, {
          threads: {},
          hasMoreThreads: false,
          loadState: 'loading',
          loadError: null,
        });

        const live$ = store.commentsService.events$().pipe(
          tap((event) => {
            if (snapshotApplied) {
              applyEvent(event);
            } else {
              buffered.push(event);
            }
          }),
        );

        const load$ = from(store.commentsService.loadThreads(parent)).pipe(
          tap(({ threads, hasMore }) => {
            patchState(store, {
              threads: toThreadMap(threads),
              hasMoreThreads: hasMore,
              loadState: 'loaded',
            });
            snapshotApplied = true;
            buffered.splice(0).forEach(applyEvent);
          }),
        );

        return merge(live$, load$).pipe(
          catchError((error: unknown) => {
            patchState(store, {
              loadState: 'error',
              loadError: describeError(error),
            });
            reportFailure('load', error);
            return EMPTY;
          }),
        );
      });

    /**
     * Connects the store to a frame or view. Pass `null` to disconnect. The
     * store stays idle while commenting is not allowed and starts as soon as
     * the flag and role allow it; it reloads after every realtime reconnect.
     */
    /** One session now, and a fresh one after every realtime reconnect. */
    const liveSessions$ = (parent: CommentParent): Observable<unknown> =>
      store.commentsService.connectionRestored$.pipe(
        startWith(null),
        switchMap(() => session$(parent)),
      );

    /** Runs sessions only while commenting is allowed; resets when it is revoked. */
    const allowedSessions$ = (parent: CommentParent): Observable<unknown> =>
      isCommentingAllowed$.pipe(
        distinctUntilChanged(),
        switchMap((allowed) => {
          if (!allowed) {
            resetForParent(parent);
            return EMPTY;
          }
          return liveSessions$(parent);
        }),
      );

    const connect = rxMethod<CommentParent | null>(
      pipe(
        distinctUntilChanged((a, b) => a?.type === b?.type && a?.id === b?.id),
        switchMap((parent) => {
          resetForParent(parent);
          return parent === null ? EMPTY : allowedSessions$(parent);
        }),
      ),
    );

    const disconnect = () => connect(null);

    /** Registers the canvas lookup used to place markers attached to graphics. */
    const setGraphicBoundsResolver = (
      resolver: GraphicBoundsResolver | null,
    ) => {
      store._graphicBoundsResolver.set(resolver);
      store._graphicBoundsVersion.update((version) => version + 1);
    };

    /**
     * Recomputes marker positions. The overlay calls this whenever graphics
     * move or resize (drag end, layout, undo); the resolver itself never
     * changes identity, so nothing else would trigger the recomputation.
     */
    const invalidateMarkerPositions = () => {
      store._graphicBoundsVersion.update((version) => version + 1);
    };

    const setCommentMode = (isOn: boolean, location: CommentModeLocation) => {
      if (!store.isCommentingAllowed() && isOn) {
        return;
      }
      if (store.isCommentModeOn() === isOn) {
        return;
      }
      patchState(store, {
        isCommentModeOn: isOn,
        ...(isOn ? { areCommentsVisible: true } : { draft: null }),
      });
      store.dispatcher.dispatch(
        commentUiEvents.commentModeToggled({ isOn, location }),
      );
    };

    const toggleCommentMode = (location: CommentModeLocation) => {
      setCommentMode(!store.isCommentModeOn(), location);
    };

    const setCommentsVisible = (
      isVisible: boolean,
      location: CommentModeLocation,
    ) => {
      if (store.areCommentsVisible() === isVisible) {
        return;
      }
      patchState(store, {
        areCommentsVisible: isVisible,
        ...(isVisible ? {} : { selectedThreadId: null, draft: null }),
      });
      store.dispatcher.dispatch(
        commentUiEvents.commentsVisibilityChanged({ isVisible, location }),
      );
    };

    const selectThread = (threadId: UUID | null) => {
      patchState(store, {
        selectedThreadId:
          threadId !== null && threadId in store.threads() ? threadId : null,
      });
    };

    const startDraft = (anchor: CommentAnchor) => {
      patchState(store, { draft: { anchor }, selectedThreadId: null });
    };

    const cancelDraft = () => {
      patchState(store, { draft: null });
    };

    /** Sends the first comment of the draft; a blank text cancels the draft. */
    const commitDraft = async (text: string): Promise<CommentThread | null> => {
      const parent = store.parent();
      const draft = store.draft();
      if (parent === null || draft === null) {
        return null;
      }
      if (text.trim().length === 0) {
        cancelDraft();
        return null;
      }
      const { coordinates, graphicId } = toStoredAnchor(
        draft.anchor,
        store._graphicBoundsResolver(),
      );
      try {
        const { thread } = await store.commentsService.createThread({
          parent,
          text,
          coordinates,
          ...(graphicId === undefined ? {} : { graphicId }),
        });
        applyThread(thread);
        patchState(store, { draft: null, selectedThreadId: thread.id });
        store.dispatcher.dispatch(
          commentApiEvents.threadCreated({
            threadId: thread.id,
            isAttachedToGraphic: graphicId !== undefined,
          }),
        );
        return thread;
      } catch (error) {
        reportFailure('createThread', error);
        return null;
      }
    };

    const reply = async (
      threadId: UUID,
      text: string,
    ): Promise<Comment | null> => {
      if (text.trim().length === 0) {
        return null;
      }
      try {
        const { comment, thread } = await store.commentsService.reply({
          threadId,
          text,
        });
        applyThread(thread);
        store.dispatcher.dispatch(commentApiEvents.replyAdded({ threadId }));
        return comment;
      } catch (error) {
        reportFailure('reply', error);
        return null;
      }
    };

    const editComment = async (
      commentId: UUID,
      text: string,
    ): Promise<boolean> => {
      if (text.trim().length === 0) {
        return false;
      }
      try {
        const { thread } = await store.commentsService.updateComment({
          id: commentId,
          text,
        });
        applyThread(thread);
        return true;
      } catch (error) {
        reportFailure('editComment', error);
        return false;
      }
    };

    /** Deletes a reply, or the whole thread when the comment is its root. */
    const deleteComment = async (comment: Comment): Promise<boolean> => {
      const thread = store.threads()[comment.threadId];
      if (thread !== undefined && rootCommentOf(thread)?.id === comment.id) {
        return deleteThread(comment.threadId);
      }
      try {
        await store.commentsService.deleteComments([comment.id]);
        if (thread !== undefined) {
          applyThread({
            ...thread,
            comments: thread.comments.filter((c) => c.id !== comment.id),
          });
        }
        return true;
      } catch (error) {
        reportFailure('deleteComment', error);
        return false;
      }
    };

    const deleteThread = async (threadId: UUID): Promise<boolean> => {
      try {
        await store.commentsService.deleteThreads([threadId]);
        const { [threadId]: _removed, ...threads } = store.threads();
        patchState(store, {
          threads,
          ...(store.selectedThreadId() === threadId
            ? { selectedThreadId: null }
            : {}),
        });
        return true;
      } catch (error) {
        reportFailure('deleteThread', error);
        return false;
      }
    };

    /**
     * Moves a marker. Detaching (dropping on blank canvas a thread that was
     * attached) is not possible yet: the API treats a missing graphic id as
     * "unchanged", so the graphic id is only sent when the new anchor has one.
     */
    // TODO: NGH-9390 send `graphicId: null` to detach once the API supports clearing it;
    // until then a marker dragged off its element keeps its old anchor.
    const moveThread = async (
      threadId: UUID,
      anchor: CommentAnchor,
    ): Promise<boolean> => {
      const { coordinates, graphicId } = toStoredAnchor(
        anchor,
        store._graphicBoundsResolver(),
      );
      try {
        const { thread } = await store.commentsService.updateThread({
          id: threadId,
          coordinates,
          ...(graphicId === undefined ? {} : { graphicId }),
        });
        applyThread(thread);
        return true;
      } catch (error) {
        reportFailure('moveThread', error);
        return false;
      }
    };

    /** Marks every comment of the thread as read for the current user. */
    const markThreadRead = async (threadId: UUID): Promise<void> => {
      const thread = store.threads()[threadId];
      const userId = store.currentUserId();
      if (thread === undefined || userId === null) {
        return;
      }
      const unread = thread.comments.filter(
        (comment) =>
          comment.author.id !== userId &&
          !(comment.readBy ?? []).includes(userId),
      );
      if (unread.length === 0) {
        return;
      }
      try {
        const { commentIds } = await store.commentsService.markAsRead({
          commentIds: unread.map((comment) => comment.id),
          threadIds: undefined,
        });
        const current = store.threads()[threadId];
        if (current === undefined) {
          return;
        }
        applyThread({
          ...current,
          comments: current.comments.map((comment) =>
            commentIds.includes(comment.id)
              ? { ...comment, readBy: [...(comment.readBy ?? []), userId] }
              : comment,
          ),
        });
      } catch (error) {
        reportFailure('markRead', error);
      }
    };

    return {
      connect,
      disconnect,
      setGraphicBoundsResolver,
      invalidateMarkerPositions,
      setCommentMode,
      toggleCommentMode,
      setCommentsVisible,
      selectThread,
      startDraft,
      cancelDraft,
      commitDraft,
      reply,
      editComment,
      deleteComment,
      deleteThread,
      moveThread,
      markThreadRead,
      applyEvent,
    };
  }),
);

export type CommentLocalStore = InstanceType<typeof CommentLocalStore>;

/** Human-readable message for a thrown value, whatever its shape. */
function describeError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error) ?? 'Unknown error';
  } catch {
    return 'Unknown error';
  }
}
